/**
 * TopBar — the persistent 56px app-shell header (ux-overhaul-hld.md §2).
 * Always rendered, never gated by sidebar collapse/hover state, so the
 * school switcher and notification bell are reachable regardless of sidebar
 * state. Left cluster: sidebar toggle + breadcrumb derived from navConfig
 * for the active route. Right cluster: SchoolSwitcher, NotificationBell,
 * ProfileMenu (in that order).
 */
import { PanelLeft } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { NotificationBell } from '@/components/notifications/NotificationBell';
import { categoryConfig, findActiveNavItem } from './navConfig';
import { ProfileMenu } from './ProfileMenu';
import { SchoolSwitcher } from './SchoolSwitcher';

interface TopBarProps {
  onToggleSidebar: () => void;
  isSidebarCollapsed: boolean;
  brandColor?: string;
}

/** Humanizes the last non-empty path segment as a breadcrumb fallback for
 * routes with no navConfig match — never renders the literal "undefined". */
function fallbackTitle(pathname: string): string | undefined {
  const segments = pathname.split('/').filter(Boolean);
  const last = segments.at(-1);
  if (!last) return undefined;
  return last
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

export function TopBar({ onToggleSidebar, isSidebarCollapsed, brandColor }: TopBarProps) {
  const location = useLocation();
  const activeItem = findActiveNavItem(location.pathname);
  const groupName = activeItem ? categoryConfig[activeItem.category].name : undefined;
  const pageName = activeItem?.name ?? fallbackTitle(location.pathname);

  return (
    <header
      data-testid="app-topbar"
      className="sticky top-0 z-30 flex h-14 shrink-0 items-center justify-between border-b border-gray-200 bg-white px-4 sm:px-6"
    >
      <div className="flex min-w-0 items-center gap-3">
        <button
          type="button"
          onClick={onToggleSidebar}
          aria-label={isSidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          aria-pressed={isSidebarCollapsed}
          className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-1"
        >
          <PanelLeft className="h-5 w-5" />
        </button>

        {pageName && (
          <nav aria-label="Breadcrumb" className="min-w-0 truncate text-sm">
            {groupName && <span className="text-gray-400">{groupName} / </span>}
            <span className="font-medium text-gray-900">{pageName}</span>
          </nav>
        )}
      </div>

      <div className="flex flex-shrink-0 items-center gap-2">
        <SchoolSwitcher />
        <NotificationBell />
        <ProfileMenu brandColor={brandColor} />
      </div>
    </header>
  );
}
